import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Heart, Building2, Car, Watch, Gift, Trash2, ArrowLeft, 
  Home, MessageCircle, ShoppingBag, ExternalLink, Package, X, MapPin, Tag, CheckCircle2
} from 'lucide-react';
import Nav from '@/components/Nav';
import QuickAccessBar from '@/components/QuickAccessBar';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '@/context/CurrencyContext';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WishlistPage = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bundleModal, setBundleModal] = useState(null);

  const fetchWishlist = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wishlist`);
      setWishlist(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const removeFromWishlist = async (itemId) => {
    try {
      await axios.delete(`${API}/wishlist/${itemId}`);
      setWishlist(wishlist.filter(item => item.item_id !== itemId && item.id !== itemId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const addToPortfolio = async (item) => {
    try {
      await axios.post(`${API}/portfolio/add`, {
        item_type: item.item_type,
        item_id: item.item_id,
        title: item.details?.title || item.title || 'Owned Asset',
        acquisition_price: item.details?.price
      });
      setWishlist(prev => prev.filter(w => w.item_id !== item.item_id));
      toast.success('Added to your portfolio');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to portfolio');
    }
  };

  const getItemIcon = (type) => {
    switch (type) {
      case 'property': return <Building2 className="w-5 h-5" />;
      case 'car':
      case 'cars': return <Car className="w-5 h-5" />;
      case 'watch':
      case 'watches': return <Watch className="w-5 h-5" />;
      case 'bundle': return <Package className="w-5 h-5" />;
      default: return <Gift className="w-5 h-5" />;
    }
  };

  const handleViewItem = (item) => {
    if (item.item_type === 'bundle') {
      setBundleModal(item);
    } else if (item.item_type === 'property') {
      navigate(`/properties/${item.item_id}`);
    } else {
      navigate(`/marketplace/${item.item_id}`);
    }
  };

  const getBundleItemIcon = (type) => {
    switch (type) {
      case 'property': return <Building2 className="w-4 h-4 text-gold" />;
      case 'car': return <Car className="w-4 h-4 text-blue-400" />;
      case 'watch': return <Watch className="w-4 h-4 text-purple-400" />;
      case 'accessory': return <Gift className="w-4 h-4 text-pink-400" />;
      default: return <Tag className="w-4 h-4 text-text-secondary" />;
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-text-primary mobile-pb">
      {/* Navigation */}
      <Nav showBack activePage="/wishlist" />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 sm:mb-12"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-red-500/20 flex items-center justify-center">
              <Heart className="w-7 h-7 text-red-400 fill-red-400" />
            </div>
            <div>
              <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-semibold">My Wishlist</h1>
              <p className="text-text-secondary mt-1">{wishlist.length} saved items</p>
            </div>
          </div>
          <QuickAccessBar />
        </motion.div>

        {/* Wishlist Content */}
        {loading ? (
          <div className="text-center py-20">
            <div className="text-gold text-xl">Loading your wishlist...</div>
          </div>
        ) : wishlist.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-12 text-center"
          >
            <Heart className="w-16 h-16 text-text-secondary mx-auto mb-6" />
            <h2 className="font-playfair text-2xl font-semibold mb-3">Your wishlist is empty</h2>
            <p className="text-text-secondary mb-8 max-w-md mx-auto">
              Save your favorite properties, cars, watches, and accessories to view them later.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/properties')} 
                className="bg-gold text-black hover:bg-gold-light"
              >
                <Building2 className="mr-2 w-4 h-4" /> Explore Properties
              </Button>
              <Button 
                onClick={() => navigate('/marketplace')} 
                variant="outline"
                className="border-white/20 hover:border-gold"
              >
                <ShoppingBag className="mr-2 w-4 h-4" /> Browse Marketplace
              </Button>
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {wishlist.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className="glass-card overflow-hidden group"
                  data-testid={`wishlist-item-${index}`}
                >
                  {/* Image */}
                  <div className="aspect-[4/3] overflow-hidden relative">
                    {item.item_type === 'bundle' && item.details?.items?.length > 0 ? (
                      // Bundle: show grid of item images
                      <div className="w-full h-full grid grid-cols-2 grid-rows-2">
                        {item.details.items.slice(0, 4).map((bi, biIdx) => (
                          <img
                            key={`bundle-item-${biIdx}`}
                            src={bi.item?.images?.[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=300'}
                            alt={bi.item?.title || bi.type}
                            className="w-full h-full object-cover"
                          />
                        ))}
                      </div>
                    ) : (
                      <img
                        src={item.details?.images?.[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600'}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    )}
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      <span className="bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full text-xs flex items-center gap-1">
                        {getItemIcon(item.item_type)}
                        <span className="capitalize">{item.item_type}</span>
                      </span>
                    </div>
                    <button
                      onClick={() => removeFromWishlist(item.item_id)}
                      className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-red-400 hover:bg-red-500 hover:text-white transition-colors"
                      title="Remove from wishlist"
                      data-testid={`wishlist-remove-btn-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="font-semibold text-lg mb-1 line-clamp-1">
                      {item.item_type === 'bundle' 
                        ? (item.bundle_name || item.details?.name || 'Lifestyle Bundle')
                        : (item.details?.title || item.details?.model || item.title || 'Saved Item')
                      }
                    </h3>
                    
                    {item.item_type === 'bundle' && item.details?.tagline && (
                      <p className="text-gold text-sm mb-2">{item.details.tagline}</p>
                    )}

                    {/* Bundle items preview */}
                    {item.item_type === 'bundle' && item.details?.items?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {item.details.items.map((bi, biIdx) => (
                          <span key={`bundle-item-${biIdx}`} className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded text-xs text-text-secondary">
                            {getBundleItemIcon(bi.type)}
                            {bi.item?.title || bi.item?.brand || bi.type}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {item.details?.location && item.item_type !== 'bundle' && (
                      <p className="text-text-secondary text-sm mb-3 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {item.details.location}
                      </p>
                    )}
                    
                    {item.details?.brand && item.item_type !== 'bundle' && (
                      <p className="text-text-secondary text-sm mb-3">{item.details.brand}</p>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-white/10">
                      <span className="text-gold font-bold text-lg">
                        {item.item_type === 'bundle' 
                          ? (item.details?.total_value ? formatPrice(item.details.total_value) : 'Bundle')
                          : formatPrice(item.details?.price)
                        }
                      </span>
                      <div className="flex items-center gap-2">
                        {item.item_type !== 'bundle' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => addToPortfolio(item)}
                            className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 text-xs"
                            title="I own this — move to portfolio"
                            data-testid={`wishlist-own-btn-${index}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> I Own This
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleViewItem(item)}
                          className="bg-gold text-black hover:bg-gold-light font-semibold"
                          data-testid={`wishlist-view-btn-${index}`}
                        >
                          {item.item_type === 'bundle' ? 'View Bundle' : 'View'} <ExternalLink className="ml-1 w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Bundle Detail Modal */}
      <AnimatePresence>
        {bundleModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setBundleModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-obsidian border border-white/10 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-auto"
              onClick={e => e.stopPropagation()}
              data-testid="bundle-detail-modal"
            >
              <div className="p-6 border-b border-white/10 flex justify-between items-center sticky top-0 bg-obsidian z-10">
                <div>
                  <h2 className="font-playfair text-2xl font-semibold">
                    {bundleModal.bundle_name || bundleModal.details?.name || 'Lifestyle Bundle'}
                  </h2>
                  {bundleModal.details?.tagline && (
                    <p className="text-gold text-sm mt-1">{bundleModal.details.tagline}</p>
                  )}
                </div>
                <button onClick={() => setBundleModal(null)} className="text-text-secondary hover:text-white" data-testid="close-bundle-modal">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6">
                {bundleModal.details?.description && (
                  <p className="text-text-secondary mb-6">{bundleModal.details.description}</p>
                )}

                {/* Bundle Items */}
                {bundleModal.details?.items?.length > 0 ? (
                  <div className="space-y-4 mb-6">
                    <h3 className="font-semibold text-lg">Bundle Contents</h3>
                    {bundleModal.details.items.map((bi, biIdx) => (
                      <div
                        key={`bundle-item-${biIdx}`}
                        className="flex gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                        onClick={() => {
                          setBundleModal(null);
                          if (bi.type === 'property') {
                            navigate(`/properties/${bi.item?.id}`);
                          } else {
                            navigate(`/marketplace/${bi.item?.id}`);
                          }
                        }}
                        data-testid={`bundle-item-${biIdx}`}
                      >
                        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={bi.item?.images?.[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200'}
                            alt={bi.item?.title || bi.type}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {getBundleItemIcon(bi.type)}
                            <span className="text-xs text-text-secondary capitalize">{bi.type}</span>
                          </div>
                          <h4 className="font-semibold truncate">{bi.item?.title || bi.item?.brand || 'Item'}</h4>
                          {bi.item?.location && (
                            <p className="text-xs text-text-secondary flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" /> {bi.item.location}
                            </p>
                          )}
                          {bi.item?.price && (
                            <p className="text-gold font-bold mt-1">{formatPrice(bi.item.price)}</p>
                          )}
                        </div>
                        <ExternalLink className="w-4 h-4 text-text-secondary flex-shrink-0 mt-2" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-text-secondary">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Bundle details are not available.</p>
                    <p className="text-sm mt-1">Try saving the bundle again from the Dashboard.</p>
                  </div>
                )}

                {/* Summary */}
                {bundleModal.details?.total_value && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-4 rounded-xl bg-gold/5 border border-gold/20">
                    <div>
                      <p className="text-xs text-text-secondary">Total Value</p>
                      <p className="text-lg font-bold text-gold">{formatPrice(bundleModal.details.total_value)}</p>
                    </div>
                    {bundleModal.details.savings && (
                      <div>
                        <p className="text-xs text-text-secondary">Your Savings</p>
                        <p className="text-lg font-bold text-emerald-400">{formatPrice(bundleModal.details.savings)}</p>
                      </div>
                    )}
                    {bundleModal.details.match_score && (
                      <div>
                        <p className="text-xs text-text-secondary">Match Score</p>
                        <p className="text-lg font-bold">{bundleModal.details.match_score}%</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WishlistPage;
